import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsTransfers, type GoodsTransferItem } from '@/api/goodsTransfers';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import NomenclatureSelect from '@/components/NomenclatureSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  comment: string;
  items: GoodsTransferItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  comment: '',
  items: [],
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
const fmtMoney = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsTransferEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по warehouse.transfer.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('warehouse.transfer.manage');
  const docQuery = useQuery({
    queryKey: ['goods-transfer', id],
    queryFn: () => goodsTransfers.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'goods-transfer', (cid) => goodsTransfers.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  // 2026-08-11 (рішення власника): попап номенклатури з мультивибором.
  const [pickerOpen, setPickerOpen] = useState(false);
  const numberPreview = useNumberPreview('doc.goods_transfers', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items,
      });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id ?? null,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          cost_amount: it.cost_amount ? String(it.cost_amount) : undefined,
          // 0413: склади рядка (fallback стара шапка для legacy-доків).
          warehouse_from_id: it.warehouse_from_id ?? d.warehouse_from_id ?? null,
          warehouse_to_id: it.warehouse_to_id ?? d.warehouse_to_id ?? null,
        })),
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalQty = form.items.reduce((s, e) => s + Number(e.quantity || 0), 0);
  const totalCost = form.items.reduce((s, e) => s + Number(e.cost_amount || 0), 0);

  const create = useMutation({
    mutationFn: () => goodsTransfers.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
      navigate(`/goods-transfers/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsTransfers.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-transfer', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти чернетку і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsTransfers.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        comment: form.comment || null,
        items: form.items,
      });
      try {
        await goodsTransfers.post(doc.id);
      } catch (e) {
        // ч.1343: документ уже створено — відкриваємо його, інакше повторне
        // «провести» створило б дубль (правило ч.1266).
        navigate(`/goods-transfers/${doc.id}`, { replace: true });
        throw e;
      }
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
      navigate(`/goods-transfers/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await goodsTransfers.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        comment: form.comment || null,
        items: form.items,
      });
      await goodsTransfers.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-transfer', id] });
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsTransfers.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-transfer', id] });
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsTransfers.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-transfers'] });
      navigate('/goods-transfers');
    },
  });

  function setItem(i: number, patch: Partial<GoodsTransferItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має warehouse.transfer.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати переміщення товарів у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-transfers')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.886 (новий дизайн, Закон #10): «ще дій» не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'items', title: 'Рядки', required: 1, filled: form.items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове переміщення' : `Переміщення: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          entriesDoc={{ type: 'doc.goods_transfers', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/goods-transfers')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          postDisabled={form.items.length === 0}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postPending={create.isPending || createAndPost.isPending || update.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/goods-transfers/${id}/print`)}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/goods-transfers/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.goods_transfers', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата *</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={2} title="Рядки"
        actions={!isPosted ? (
          <button type="button" className="primary" onClick={() => setPickerOpen(true)}
            title="Підбір з довідника з залишками; галочками можна обрати кілька товарів одразу">
            + додати номенклатуру
          </button>
        ) : undefined}>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Номенклатура</th>
            <th style={{ width: 190 }}>Зі складу</th>
            <th style={{ width: 190 }}>На склад</th>
            <th style={{ width: 120 }}>Кількість</th>
            {isPosted && <th style={{ width: 140 }}>Собівартість</th>}
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i}>
              <td>{it.line_number}</td>
              <td>
                <NomenclatureSelect
                  value={it.nomenclature_id}
                  disabled={isPosted}
                  onChange={(v) => setItem(i, { nomenclature_id: v })}
                />
              </td>
              <td>
                <select value={it.warehouse_from_id ?? ''}
                  onChange={(e) => setItem(i, { warehouse_from_id: e.target.value || null })}
                  disabled={isPosted}>
                  <option value="">—</option>
                  {warehousesQ.data?.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </td>
              <td>
                <select value={it.warehouse_to_id ?? ''}
                  onChange={(e) => setItem(i, { warehouse_to_id: e.target.value || null })}
                  disabled={isPosted}>
                  <option value="">—</option>
                  {warehousesQ.data?.filter((w) => w.id !== it.warehouse_from_id).map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="number" step="0.001" value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: e.target.value })}
                  disabled={isPosted}
                />
              </td>
              {isPosted && (
                <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                  {fmtMoney(Number(it.cost_amount ?? 0))}
                </td>
              )}
              <td>
                {!isPosted && (
                  <button
                    onClick={() => removeRow(i)}
                    className="danger"
                    style={{ padding: '0.3rem 0.6rem' }}
                  >×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ textAlign: 'right' }}>Усього:</td>
            <td style={{ textAlign: 'right' }}>{fmt(totalQty)}</td>
            {isPosted && (
              <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>
                {fmtMoney(totalCost)}
              </td>
            )}
            <td></td>
          </tr>
        </tfoot>
      </TchTable>
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          multiSelect
          onPickMany={(rows) => {
            setForm((f) => ({
              ...f,
              items: [...f.items, ...rows.map((r, k) => ({
                line_number: f.items.length + k + 1,
                nomenclature_id: r.nomenclature_id,
                quantity: '1',
                // Джерело — склад рядка залишків (якщо був обраний).
                warehouse_from_id: r.warehouse_id || null,
                warehouse_to_id: null,
              }))],
            }));
            setPickerOpen(false);
          }}
          onPick={() => undefined}
          onClose={() => setPickerOpen(false)}
        />
      )}

      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.goods_transfers" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
      {isPosted && (
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          Внутрішнє переміщення: бухгалтерські проводки не формуються
          (товар залишається у власності організації, рахунок 281 не змінюється).
          Рухи проходять у регістрах «Товари на складах» і «Партії товарів».
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
